import React, { useEffect, useRef, useState } from 'react';
import './CursorEffect.css';

const INTERACTIVE_SELECTOR = 'button, a, input, select, textarea, [role="button"], [tabindex], label, summary';

const CursorEffect: React.FC = () => {
  const dotRef = useRef<HTMLDivElement>(null);
  const ringRef = useRef<HTMLDivElement>(null);
  const hovering = useRef(false);
  const clicking = useRef(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Skip on touch-only devices
    const isTouch = !window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (isTouch) return;

    document.body.classList.add('cursor-active');

    let mouseX = 0;
    let mouseY = 0;
    let ringX = 0;
    let ringY = 0;
    let rafId = 0;
    let entered = false;

    const updateDot = (e: MouseEvent) => {
      mouseX = e.clientX;
      mouseY = e.clientY;

      if (!entered) {
        entered = true;
        setVisible(true);
        ringX = mouseX;
        ringY = mouseY;
      }

      if (dotRef.current) {
        dotRef.current.style.transform = `translate(${mouseX - 3}px, ${mouseY - 3}px)`;
      }

      const target = e.target as Element;
      const interactive = !!target?.closest?.(INTERACTIVE_SELECTOR);
      if (interactive !== hovering.current) {
        hovering.current = interactive;
        setIsHovering(interactive);
      }
    };

    const animateRing = () => {
      ringX += (mouseX - ringX) * 0.14;
      ringY += (mouseY - ringY) * 0.14;
      if (ringRef.current) {
        const offset = clicking.current ? 11 : hovering.current ? 21 : 16;
        ringRef.current.style.transform = `translate(${ringX - offset}px, ${ringY - offset}px)`;
      }
      rafId = requestAnimationFrame(animateRing);
    };

    const onMouseDown = () => {
      clicking.current = true;
      setIsClicking(true);
    };
    const onMouseUp = () => {
      clicking.current = false;
      setIsClicking(false);
    };
    const onMouseLeave = () => setVisible(false);
    const onMouseEnter = () => setVisible(true);

    document.addEventListener('mousemove', updateDot, { passive: true });
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mouseleave', onMouseLeave);
    document.addEventListener('mouseenter', onMouseEnter);
    rafId = requestAnimationFrame(animateRing);

    return () => {
      document.body.classList.remove('cursor-active');
      document.removeEventListener('mousemove', updateDot);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mouseleave', onMouseLeave);
      document.removeEventListener('mouseenter', onMouseEnter);
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <>
      <div
        ref={dotRef}
        className={[
          'cursor-dot',
          isHovering ? 'cursor-dot--hover' : '',
          isClicking ? 'cursor-dot--click' : '',
        ].join(' ')}
        style={{ opacity: visible ? 1 : 0 }}
        aria-hidden="true"
      />
      <div
        ref={ringRef}
        className={[
          'cursor-ring',
          isHovering ? 'cursor-ring--hover' : '',
          isClicking ? 'cursor-ring--click' : '',
        ].join(' ')}
        style={{ opacity: visible ? 1 : 0 }}
        aria-hidden="true"
      />
    </>
  );
};

export default CursorEffect;
